import classNames from "classnames";
import { XCloseIcon } from "@vetty/icons";
import { Modal } from "@/components";
import {
  formatFormApplicationTimestamp,
  getFormApplicationTypeMeta,
} from "../../_constants/formsApplications.const";
import { FormApplication } from "../../_types/formsApplications.types";
import styles from "./FormsApplicationDetailsModal.module.scss";

type FormsApplicationDetailsModalProps = {
  application: FormApplication | null;
  isOpen: boolean;
  isUpdatingReadStatus: boolean;
  onClose: () => void;
};

export const FormsApplicationDetailsModal = ({
  application,
  isOpen,
  isUpdatingReadStatus,
  onClose,
}: FormsApplicationDetailsModalProps) => {
  if (!application) return null;

  const typeMeta = getFormApplicationTypeMeta(application.type);
  const hasReadDetails = Boolean(application.read && application.readByName && application.readAt);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles["comp"]}>
        <div className={styles["comp__header"]}>
          <div className={styles["comp__title-row"]}>
            <div className={styles["comp__heading"]}>{application.fullName}</div>
            <span
              className={classNames(
                styles["comp__badge"],
                styles[`comp__badge--${typeMeta.variant}`]
              )}
            >
              {typeMeta.label}
            </span>
          </div>

          <button
            type="button"
            className={styles["comp__close-button"]}
            onClick={onClose}
            aria-label="Close form application details"
          >
            <XCloseIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <div className={styles["comp__meta-grid"]}>
          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Email</span>
            <span className={styles["comp__meta-value"]}>{application.email}</span>
          </div>
          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Status</span>
            <div className={styles["comp__meta-stack"]}>
              <span className={styles["comp__meta-value"]}>
                {application.read ? "Read" : "Unread"}
              </span>
              {hasReadDetails && (
                <span className={styles["comp__meta-note"]}>
                  Read by {application.readByName} at{" "}
                  {formatFormApplicationTimestamp(application.readAt as number)}
                </span>
              )}
              {isUpdatingReadStatus && (
                <span className={styles["comp__meta-note"]}>Updating read status...</span>
              )}
            </div>
          </div>
          <div className={styles["comp__meta-item"]}>
            <span className={styles["comp__meta-label"]}>Date</span>
            <span className={styles["comp__meta-value"]}>
              {formatFormApplicationTimestamp(application.createdAt)}
            </span>
          </div>
          {application.phone && (
            <div className={styles["comp__meta-item"]}>
              <span className={styles["comp__meta-label"]}>Phone</span>
              <span className={styles["comp__meta-value"]}>{application.phone}</span>
            </div>
          )}
          {application.company && (
            <div className={styles["comp__meta-item"]}>
              <span className={styles["comp__meta-label"]}>Company</span>
              <span className={styles["comp__meta-value"]}>{application.company}</span>
            </div>
          )}
        </div>

        <div className={styles["comp__content"]}>
          <div className={styles["comp__meta-label"]}>Content</div>
          <p>{application.content}</p>
        </div>
      </div>
    </Modal>
  );
};
